import React, { useCallback, useState } from 'react'
import { Form } from 'react-bootstrap'
import { firstApiErrorMessage } from '../../../models/ApiErrors'
import { Modal } from '../../../components/Modal/Modal'
import { Button } from '../../../components/Button/Button'
import { CredentialField } from '../components/CredentialField'
import { DataSourceDocsLink } from './DataSourceDocsLink'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import { useCreateDataSourceMutation } from '../../../store/integrations/externalDataSources'
import { type CreatedExternalDataSource } from '../../../models/ExternalDataSource'
import { DataSourceFormFields } from './DataSourceFormFields'
import { type DataSourceFormValues, EMPTY_DATA_SOURCE_FORM, isDataSourceFormValid, parseTimeout } from './dataSourceForm'

interface RegisterDataSourceModalProps {
  show: boolean
  clientId: string
  onHide: () => void
}

type Step = 'form' | 'reveal'

export const RegisterDataSourceModal: React.FC<RegisterDataSourceModalProps> = ({ show, clientId, onHide }) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const [createDataSource, { isLoading }] = useCreateDataSourceMutation()

  const [step, setStep] = useState<Step>('form')
  const [created, setCreated] = useState<CreatedExternalDataSource | null>(null)
  const [validated, setValidated] = useState<boolean>(false)
  const [values, setValues] = useState<DataSourceFormValues>(EMPTY_DATA_SOURCE_FORM)

  const handleClose = useCallback(() => {
    setStep('form')
    setCreated(null)
    setValidated(false)
    setValues(EMPTY_DATA_SOURCE_FORM)
    onHide()
  }, [onHide])

  const handleSubmit = useCallback(async () => {
    setValidated(true)
    if (!isDataSourceFormValid(values)) {
      return
    }
    try {
      const response = await createDataSource({
        accountId,
        clientId,
        payload: {
          name: values.name.trim(),
          description: values.description.trim() === '' ? null : values.description.trim(),
          callbackUrl: values.callbackUrl.trim(),
          dataTypes: values.dataTypes,
          contractVersion: values.contractVersion,
          auth: values.useApiKey ? { type: 'apiKey', headerName: values.headerName.trim(), apiKey: values.apiKey.trim() } : null,
          fetchPolicy: { timeoutMs: parseTimeout(values) },
        },
      }).unwrap()
      setCreated(response.data)
      setStep('reveal')
    } catch (error) {
      dispatch(
        addToast({
          variant: 'error',
          title: 'Could not register the data source',
          messages: [firstApiErrorMessage(error) ?? 'Please try again.'],
        }),
      )
    }
  }, [values, createDataSource, accountId, clientId, dispatch])

  if (step === 'reveal' && created) {
    return (
      <Modal
        show={show}
        backdrop='static'
        keyboard={false}
        header='Data source registered'
        body={
          <div>
            <div className='d-flex align-items-start zocks-gap-0.5 p-3 mb-3 rounded-2 zocks-bg-color-warning'>
              <i className='bi bi-exclamation-triangle-fill zocks-text-warning-text flex-shrink-0' />
              <p className='paragraph-small mb-0'>
                Shown once. Zocks stores only an encrypted copy — if you lose it, the only way back is to register the data source again.
              </p>
            </div>
            <CredentialField label='Signing secret' value={created.signingSecret} copyable masked />
            <p className='paragraph-small zocks-text-gray-600 mt-3 mb-0'>
              Sign every reply with it: HMAC-SHA256 over <code>{'"{timestamp}.{rawBody}"'}</code>, returned as <code>X-Zocks-Signature: v1=…</code>
            </p>
          </div>
        }
        footer={
          <div className='w-100 d-flex justify-content-center'>
            <Button variant='primary' onClick={handleClose}>
              I&apos;ve saved it, continue
            </Button>
          </div>
        }
      />
    )
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      header={
        <span className='d-flex align-items-center justify-content-between flex-grow-1 zocks-gap-1 me-2'>
          Register a data source
          <DataSourceDocsLink />
        </span>
      }
      body={
        <Form>
          <DataSourceFormFields values={values} onChange={setValues} validated={validated} />
        </Form>
      }
      showFooterActions
      footerPrimaryActionTitle='Register'
      footerSecondaryActionTitle='Cancel'
      onFooterPrimaryAction={handleSubmit}
      onFooterSecondaryAction={handleClose}
      footerPrimaryActionLoading={isLoading}
      footerPrimaryActionDisabled={isLoading}
    />
  )
}
